<template>
  <div class="session-card">
    <div class="session-card__header">
      <div class="session-card__date">
        <span class="date-text">{{ formattedDate }}</span>
      </div>
      <div class="session-card__status">
        <span class="status-indicator"></span>
      </div>
    </div>
    
    <div class="session-card__content">
      <div class="session-card__location">
        <svg class="location-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>
        <span class="location-text">{{ session.location || 'Location not set' }}</span>
      </div>

      <div v-if="session.notes" class="session-card__notes">
        <svg class="notes-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
        </svg>
        <span class="notes-text">{{ session.notes }}</span>
      </div>

      <div class="session-card__stats">
        <div class="stat-item">
          <svg class="stat-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
          <span class="stat-text">{{ albumCount }} albums</span>
        </div>
      </div>
    </div>

    <div class="session-card__actions">
      <button class="edit-button" @click="navigateToEdit">
        <svg class="edit-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
        </svg>
        Edit
      </button>
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();

const props = defineProps({
  session: {
    type: Object,
    required: true,
    validator: (session) => session.id && session.session_date
  }
});

const navigateToEdit = () => {
  router.push(`/sessions/${props.session.id}/edit`);
};

const formattedDate = computed(() => {
  if (!props.session.session_date) return 'Date not set';
  
  try {
    const date = new Date(props.session.session_date);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    return props.session.session_date;
  }
});

const albumCount = computed(() => {
  // Placeholder - this could be enhanced to show actual album count
  // when the backend provides this information
  return props.session.album_count || 0;
});
</script>

<style scoped>
.session-card {
  background: #232323;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
}

.session-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-color: rgba(255, 255, 255, 0.2);
}

.session-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #2d8cf0, #1867c0);
  transition: height 0.3s ease;
}

.session-card:hover::before {
  height: 6px;
}

.session-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.session-card__date {
  flex: 1;
}

.date-text {
  font-size: 1.25rem;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: -0.01em;
}

.session-card__status {
  display: flex;
  align-items: center;
}

.status-indicator {
  width: 8px;
  height: 8px;
  background: #4caf50;
  border-radius: 50%;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.session-card__content {
  margin-bottom: 1.5rem;
}

.session-card__location {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  color: #b0b0b0;
}

.location-icon {
  width: 16px;
  height: 16px;
  margin-right: 0.5rem;
  color: #666;
}

.location-text {
  font-size: 0.95rem;
  font-weight: 500;
}

.session-card__notes {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  border-left: 3px solid #2d8cf0;
}

.notes-icon {
  width: 16px;
  height: 16px;
  margin-right: 0.5rem;
  margin-top: 0.1rem;
  color: #2d8cf0;
  flex-shrink: 0;
}

.notes-text {
  font-size: 0.9rem;
  font-weight: 400;
  color: #d0d0d0;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.session-card__stats {
  display: flex;
  gap: 1rem;
}

.stat-item {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.stat-icon {
  width: 14px;
  height: 14px;
  margin-right: 0.5rem;
  color: #ffc107;
}

.stat-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #e0e0e0;
}

.session-card__actions {
  display: flex;
  justify-content: flex-end;
}

.edit-button {
  display: flex;
  align-items: center;
  background: #2d8cf0;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.25rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(45, 140, 240, 0.2);
}

.edit-button:hover {
  background: #1867c0;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.3);
}

.edit-button:active {
  transform: translateY(0);
}

.edit-icon {
  width: 16px;
  height: 16px;
  margin-right: 0.5rem;
}

/* Large screens - enhance card content for wider spaces */
@media (min-width: 1200px) {
  .session-card {
    padding: 2rem;
  }
  
  .session-card__header {
    margin-bottom: 1.5rem;
  }
  
  .date-text {
    font-size: 1.375rem;
  }
  
  .session-card__content {
    margin-bottom: 2rem;
  }
  
  .session-card__stats {
    gap: 1.5rem;
  }
  
  .stat-item {
    padding: 0.75rem 1rem;
  }
  
  .edit-button {
    padding: 1rem 1.5rem;
    font-size: 1rem;
  }
}

@media (max-width: 600px) {
  .session-card {
    padding: 1rem;
  }
  
  .session-card__header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .date-text {
    font-size: 1.1rem;
  }
  
  .session-card__stats {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .edit-button {
    width: 100%;
    justify-content: center;
  }
}
</style> 